import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Upload, Loader2, X, ImagePlus, Star } from "lucide-react";
import { api, authHeader, API } from "../lib/api";
import { SERVICES } from "../lib/site";

const CATEGORIES = SERVICES.map((s) => s.name);

function imageUrl(url) {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // /api/uploads/... served by backend via ingress
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function ProjectsManager() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} for new | project for edit

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/projects", { headers: authHeader() });
            setProjects(data);
        } catch {
            toast.error("Failed to load projects");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
        try {
            await api.delete(`/admin/projects/${p.id}`, { headers: authHeader() });
            toast.success("Project deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const toggleFeature = async (p) => {
        try {
            await api.patch(
                `/admin/projects/${p.id}/feature`,
                { featured: !p.featured },
                { headers: authHeader() }
            );
            toast.success(!p.featured ? "Project featured on homepage" : "Removed from featured");
            load();
        } catch {
            toast.error("Update failed");
        }
    };

    return (
        <div data-testid="admin-projects-tab">
            <div className="p-5 border-b border-border flex items-center justify-between">
                <div>
                    <h3 className="font-heading text-xl font-bold">Project Gallery</h3>
                    <p className="text-sm text-muted-foreground">Manage the photos shown on the public site.</p>
                </div>
                <button onClick={() => setEditing({})} className="btn-primary !py-2.5 !px-5 text-sm" data-testid="admin-project-new-btn">
                    <Plus size={16} /> New project
                </button>
            </div>

            {loading ? (
                <div className="p-12 text-center text-muted-foreground">Loading…</div>
            ) : projects.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">No projects yet.</div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
                    {projects.map((p) => (
                        <article key={p.id} className={`border rounded-lg overflow-hidden bg-background relative ${p.featured ? "border-secondary shadow-md" : "border-border"}`} data-testid={`admin-project-card-${p.id}`}>
                            {p.featured && (
                                <div className="absolute top-2 left-2 z-10 bg-secondary text-secondary-foreground px-2.5 py-1 rounded-md text-[10px] font-medium uppercase tracking-[0.18em] flex items-center gap-1">
                                    <Star size={10} fill="currentColor" /> Featured
                                </div>
                            )}
                            <div className="aspect-[4/3] bg-muted overflow-hidden">
                                <img src={imageUrl(p.image)} alt={p.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-4">
                                <div className="text-xs uppercase tracking-[0.18em] text-secondary font-medium">{p.category}</div>
                                <h4 className="mt-1 font-heading font-bold text-foreground truncate">{p.title}</h4>
                                <p className="text-xs text-muted-foreground mt-1">{p.location}</p>
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() => toggleFeature(p)}
                                        className={`border text-xs py-2 px-2 rounded-md flex items-center justify-center gap-1.5 transition-colors ${p.featured ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary hover:text-secondary"}`}
                                        data-testid={`admin-project-feature-${p.id}`}
                                        title={p.featured ? "Remove featured" : "Feature on homepage"}
                                    >
                                        <Star size={12} fill={p.featured ? "currentColor" : "none"} />
                                    </button>
                                    <button onClick={() => setEditing(p)} className="flex-1 border border-border hover:border-primary text-xs py-2 rounded-md flex items-center justify-center gap-1.5" data-testid={`admin-project-edit-${p.id}`}>
                                        <Edit size={12} /> Edit
                                    </button>
                                    <button onClick={() => remove(p)} className="flex-1 border border-border hover:border-destructive hover:text-destructive text-xs py-2 rounded-md flex items-center justify-center gap-1.5" data-testid={`admin-project-delete-${p.id}`}>
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {editing !== null && (
                <ProjectEditor
                    project={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function ProjectEditor({ project, onClose, onSaved }) {
    const isNew = !project.id;
    const [form, setForm] = useState({
        title: project.title || "",
        category: project.category || CATEGORIES[0],
        location: project.location || "",
        image: project.image || "",
        description: project.description || "",
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/admin/upload", fd, {
                headers: authHeader(),
            });
            setForm((f) => ({ ...f, image: data.url }));
            toast.success("Image uploaded");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Upload failed";
            toast.error(msg);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title || !form.image || !form.description || !form.location) {
            toast.error("Title, location, image and description are required.");
            return;
        }
        setSaving(true);
        try {
            if (isNew) {
                await api.post("/admin/projects", form, { headers: authHeader() });
                toast.success("Project created");
            } else {
                await api.put(`/admin/projects/${project.id}`, form, { headers: authHeader() });
                toast.success("Project updated");
            }
            onSaved();
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
            <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-card rounded-xl border border-border overflow-hidden flex flex-col max-h-[90vh]" data-testid="admin-project-editor">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading text-xl font-bold">{isNew ? "New project" : "Edit project"}</h3>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="admin-project-editor-close">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    <div className="space-y-2">
                        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">Image</label>
                        {form.image ? (
                            <div className="relative aspect-video rounded-lg overflow-hidden border border-border bg-muted">
                                <img src={imageUrl(form.image)} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => setForm({ ...form, image: "" })} className="absolute top-2 right-2 bg-background rounded-full p-1.5 shadow-md hover:text-destructive" data-testid="admin-project-image-clear">
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <label className="aspect-video rounded-lg border-2 border-dashed border-border bg-muted/50 hover:bg-muted cursor-pointer flex flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="admin-project-image-upload">
                                {uploading ? <Loader2 className="animate-spin" size={28} /> : <ImagePlus size={28} strokeWidth={1.5} />}
                                <span className="text-sm font-medium">{uploading ? "Uploading…" : "Click to upload photo"}</span>
                                <span className="text-xs">JPG · PNG · WEBP · max 8MB</span>
                                <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                            </label>
                        )}
                        <div className="text-xs text-muted-foreground">
                            Or paste an image URL:
                            <input type="url" value={form.image} onChange={update("image")} className="w-full mt-1 bg-background border border-border rounded-md px-3 py-2 text-sm" placeholder="https://…" data-testid="admin-project-image-url" />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">Title *</span>
                            <input value={form.title} onChange={update("title")} required className="mt-1.5 w-full bg-background border border-border rounded-md px-3 py-2.5 text-sm" data-testid="admin-project-title" />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">Category *</span>
                            <select value={form.category} onChange={update("category")} className="mt-1.5 w-full bg-background border border-border rounded-md px-3 py-2.5 text-sm" data-testid="admin-project-category">
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                <option value="Other">Other</option>
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">Location *</span>
                        <input value={form.location} onChange={update("location")} required placeholder="e.g. Wokingham, Berkshire" className="mt-1.5 w-full bg-background border border-border rounded-md px-3 py-2.5 text-sm" data-testid="admin-project-location" />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">Description *</span>
                        <textarea rows={4} value={form.description} onChange={update("description")} required className="mt-1.5 w-full bg-background border border-border rounded-md px-3 py-2.5 text-sm resize-none" data-testid="admin-project-description" />
                    </label>
                </div>
                <div className="p-5 border-t border-border flex gap-3 justify-end">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-border hover:bg-muted text-sm" data-testid="admin-project-cancel">Cancel</button>
                    <button type="submit" disabled={saving || uploading} className="btn-primary !py-2.5 !px-6 text-sm disabled:opacity-60" data-testid="admin-project-save">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : null}
                        {saving ? "Saving…" : isNew ? "Create project" : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}
